import { h } from 'preact'
import { useEffect, useRef } from 'preact/hooks'
import clsx from 'clsx'

interface PromolineData {
  title: string
  body?: string
  cta?: {
    text?: string
    url?: string
  }
  text_colour: string
  background_colour?: string
  priority: number
  end_date?: string
  countdown?: string[]
}

interface PromolineProps {
  promoline: PromolineData
  promolineSize?: 'small' | 'medium' | 'large'
  title?: string
  body?: string
  cta_text?: string
  cta_url?: string
  text_colour?: string
  bg_colour?: string
  end_date?: string
  countdown?: string[]
}

export const Promoline = ({
  promoline,
  promolineSize = 'small',
  title: titleOverride,
  body: bodyOverride,
  cta_text: ctaTextOverride,
  cta_url: ctaUrlOverride,
  text_colour: textColourOverride,
  bg_colour: bgColourOverride,
  end_date: endDateOverride,
  countdown: countdownOverride,
  className
}: PromolineProps & { className?: string }) => {
  if (!promoline || !promoline.title) return null

  const title = titleOverride ?? promoline.title
  const body = bodyOverride ?? promoline.body
  const ctaText = ctaTextOverride ?? promoline.cta?.text
  const ctaUrl = ctaUrlOverride ?? promoline.cta?.url
  const textColour = textColourOverride ?? promoline.text_colour
  const bgColour = bgColourOverride ?? promoline.background_colour
  const endDate = endDateOverride ?? promoline.end_date
  const countdown = countdownOverride ?? promoline.countdown

  const countdownString = Array.isArray(countdown) ? countdown.join(',') : ''

  const shouldShowCountdown = (() => {
    if (!endDate || !countdownString) return false

    switch (promolineSize) {
      case 'small':
        return countdownString.includes('PLP')
      case 'medium':
        return countdownString.includes('Minicart')
      case 'large':
        return countdownString.includes('PDP')
      default:
        return false
    }
  })()

  const getSizeConfig = () => {
    switch (promolineSize) {
      case 'small':
        return {
          container: 'w-fit',
          containerBg: 'px-0.5 py-0.25',
          titleClass: 'text-xs font-medium',
          countdownLayout: 'small',
          countdownPosition: 'inline'
        }
      case 'medium':
        return {
          container: 'w-full',
          containerBg: 'px-0.5 py-0.25',
          titleClass: 'text-xs font-medium',
          countdownLayout: 'small',
          countdownPosition: 'block'
        }
      case 'large':
        return {
          container: 'w-full',
          containerBg: shouldShowCountdown ? 'p-1' : 'p-1 lg:py-0.5',
          titleClass: 'paragraph-s font-bold',
          countdownLayout: 'medium',
          countdownPosition: 'block'
        }
      default:
        return {
          container: 'w-fit',
          containerBg: 'px-0.5 py-0.25',
          titleClass: 'paragraph-xs font-medium',
          countdownLayout: 'small',
          countdownPosition: 'inline'
        }
    }
  }

  const sizeConfig = getSizeConfig()
  const Component = ctaUrl ? 'a' : 'div'

  const componentProps = ctaUrl ? {
    href: ctaUrl,
    'aria-label': title
  } : {}

  const CountdownTimer = ({
    endDate,
    layout = 'small'
  }: {
    endDate: string
    layout?: 'small' | 'medium' | 'large'
  }) => {
    const containerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      if (containerRef.current && endDate) {
        containerRef.current.innerHTML = ''

        const countdownElement = document.createElement('x-countdown')
        countdownElement.setAttribute('data-end-date', new Date(endDate).toISOString())
        countdownElement.className = 'shrink-0 group/countdown'
        countdownElement.setAttribute('data-ui', 'visible')

        const wrapperClass = layout === 'small' ? 'flex gap-x-0.125' : 'flex gap-x-1.25'

        countdownElement.innerHTML = `
          <div class="hidden group-data-visible/countdown:flex ${wrapperClass}">
            <div class="flex">
              <span data-countdown-days class="paragraph-xs font-bold">00</span>
              <span class="paragraph-xs font-bold">${layout === 'small' ? 'd,' : 'Days'}</span>
            </div>
            <div class="flex">
              <span data-countdown-hours class="paragraph-xs font-bold">00</span>
              <span class="paragraph-xs font-bold">${layout === 'small' ? 'h,' : 'Hrs'}</span>
            </div>
            <div class="flex">
              <span data-countdown-minutes class="paragraph-xs font-bold">00</span>
              <span class="paragraph-xs font-bold">${layout === 'small' ? 'm,' : 'Mins'}</span>
            </div>
            <div class="flex">
              <span data-countdown-seconds class="paragraph-xs font-bold">00</span>
              <span class="paragraph-xs font-bold">${layout === 'small' ? 's' : 'Secs'}</span>
            </div>
          </div>
        `

        containerRef.current.appendChild(countdownElement)
      }
    }, [endDate, layout])

    return <div ref={containerRef} className="shrink-0" />
  }

  return (
    <Component
      {...componentProps}
      className={clsx(
        'shrink-0 flex items-center justify-between gap-x-0.5 gap-y-0.25 h-fit',
        sizeConfig.container,
        'text-[--text-colour] bg-[--bg-colour]',
        {
          [`${sizeConfig.containerBg} rounded-promoline overflow-hidden`]: bgColour,
          'link-fade': ctaUrl,
          'flex-wrap': !shouldShowCountdown
        },
        className
      )}
      style={{
        '--text-colour': textColour,
        '--bg-colour': bgColour || 'transparent'
      } as React.CSSProperties}
    >
      <div className={clsx(
        'grow flex gap-x-0.5 gap-y-0.25',
        sizeConfig.container,
        shouldShowCountdown && sizeConfig.countdownPosition === 'block' ? 'flex-col' : 'flex-wrap'
      )}>
        {shouldShowCountdown && sizeConfig.countdownPosition === 'inline' && endDate ? (
          <div className="shrink-0 flex items-center">
            <span className={clsx('shrink-0', sizeConfig.titleClass)}>
              {title}&nbsp;|&nbsp;
            </span>
            <CountdownTimer
              endDate={endDate}
              layout={sizeConfig.countdownLayout as 'small' | 'medium' | 'large'}
            />
          </div>
        ) : (
          <span className={clsx('shrink-0', sizeConfig.titleClass)}>
            {title}
          </span>
        )}

        <div className={clsx(
          'shrink-0 flex gap-x-0.5 gap-y-0.125 empty:hidden',
          shouldShowCountdown ? 'flex-col' : 'items-center justify-end',
          promolineSize !== 'small' ? 'grow' : ''
        )}>
          {body && promolineSize === 'large' && (
            <span className="grow paragraph-xs">
              {body}
            </span>
          )}
          {!shouldShowCountdown && ctaText && promolineSize !== 'small' && (
            <span className="shrink-0 paragraph-xs text-line">
              {ctaText}
            </span>
          )}
        </div>
      </div>

      {shouldShowCountdown && sizeConfig.countdownPosition === 'block' && endDate && (
        <CountdownTimer
          endDate={endDate}
          layout={sizeConfig.countdownLayout as 'small' | 'medium' | 'large'}
        />
      )}
    </Component>
  )
}
